import { select } from '@angular-redux/store';
import {
  AbstractType,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  InjectionToken,
  Injector,
  OnInit,
  Type,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { lazyInject } from '@app/containers/base/decorators';
import { rx } from '@app/shared/rx/rx-wrapper';
import { CompanyUtilService, EventsService } from '@app/shared/services';
import { EventTypeService } from '@app/shared/services/event-type';
import { IApiListResponse, IBaseEntityType, IEvent } from '@app/shared/types';
import { EVENT_STATUS_VALUE } from '@app/shared/types/enums/event-status.enum';
import { MAX_PAGE_SIZE, TIME_FORMATS } from '@config/index';
import { forEach, get, isEmpty, isFunction, last, slice } from 'lodash';
import { PageChangedEvent } from 'ngx-bootstrap/pagination';
import { Observable } from 'rxjs';
import { withDndGlobalLoader } from '../xux-dnd/decorators/xux-dnd-loader.decorators';
import { toMoment } from '@shared/utils';
import { XVII18nService } from '@app/i18n/services/xvi-translate.service';
import { I18n } from '@app/i18n/strings-keys';

interface IEventFilter {
  name: string;
  value: number;
  active: boolean;
}

@Component({
  selector: 'event-summary',
  templateUrl: 'event-summary.component.html',
  styleUrls: ['./event-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventSummaryComponent implements OnInit {
  @lazyInject(EventTypeService) public eventTypeService: EventTypeService;
  @lazyInject(CompanyUtilService) public companyUtilService: CompanyUtilService;
  @lazyInject(EventsService) public eventsService: EventsService;
  @lazyInject(XVII18nService) public i18nService: XVII18nService;

  @select(['ui', 'loader']) public globalLoader$: Observable<boolean>;
  public filters: IEventFilter[] = [];
  public events: IEvent[] = [];
  public currentPageEvents: IEvent[] = [];
  public total: number = 0;
  public pageSize: number = 5;
  public maxSize: number = 10;
  public isPending: boolean = false;
  public currentPage: number = 1;
  public I18n: typeof I18n = I18n;

  private selectedFilter: IEventFilter;
  private eventTypes: IBaseEntityType[] = null;

  public constructor(
    public injector: Injector,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
  ) {}

  public ngOnInit(): void {
    this.filters = [
      {
        name: this.i18nService.instant(I18n.Common.NextXEvents, { count: 5 }),
        value: 5,
        active: false,
      },
      {
        name: this.i18nService.instant(I18n.Common.NextXEvents, { count: 10 }),
        value: 10,
        active: false,
      },
      {
        name: this.i18nService.instant(I18n.Common.NextXEvents, { count: 20 }),
        value: 20,
        active: false,
      },
      {
        name: this.i18nService.instant(I18n.Common.All),
        value: 0,
        active: true,
      },
    ];

    this.selectedFilter = last(this.filters);

    this.getEventsByFilter();
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public handleFilter = (item: IEventFilter): void => {
    forEach(this.filters, (filter) => {
      filter.active = filter.value === item.value;
    });
    this.selectedFilter = item;
    this.getEventsByFilter(item.value);
    this.currentPage = 1;
  };

  public onPageChanged = (event: PageChangedEvent): void => {
    const skip = (event.page - 1) * this.pageSize;
    const target = skip + this.pageSize;
    this.currentPageEvents = slice(this.events, skip, target);
  };

  @withDndGlobalLoader('events')
  private async getEventsByFilter(
    total: number = MAX_PAGE_SIZE,
  ): Promise<void> {
    const dateFrom = toMoment().toISOString();
    const params = {
      top: total,
      filter: `event_days/start_date_time ge ${dateFrom}`,
      orderby: 'event_days/start_date_time',
    };
    this.isPending = true;
    if (!this.eventTypes) {
      try {
        const { items: eventTypes } = await this.eventTypeService
          .fetchList({}, { notCheckPermissionError: true })
          .toPromise();
        this.eventTypes = eventTypes;
      } catch (error) {}
    }
    const eventsRes: IApiListResponse = await this.eventsService
      .fetchList(params)
      .pipe(rx.take(1))
      .toPromise();

    const openEvents = [];
    const otherEvents = [];
    forEach(eventsRes?.items, (item) => {
      const startDateTime = item.event_days[0].start_date_time;
      const endDateTime = item.event_days[0].end_date_time;
      const eventStartDateTime =
        this.companyUtilService.convertUTCDateWithTimeZone(
          startDateTime,
          true,
          TIME_FORMATS.LT,
        );
      const eventEndDateTime =
        this.companyUtilService.convertUTCDateWithTimeZone(
          endDateTime,
          true,
          TIME_FORMATS.LT,
        );
      item.durationTime = `${eventStartDateTime} - ${eventEndDateTime}`;
      // Update event type name and image
      forEach(this.eventTypes, (eventType) => {
        if (eventType.entity_id === item.event_type_entity_id) {
          const eventTypeUrl = get(eventType, 'images[0].source_url', '');
          if (eventTypeUrl) {
            item.typeSrc =
              this.sanitizer.bypassSecurityTrustResourceUrl(eventTypeUrl);
          }
          item.typeName = get(eventType, 'name', '');
        }
      });
      // Formatter month/day/week for event start date time
      item.month = this.companyUtilService.convertUTCDateWithTimeZone(
        startDateTime,
        true,
        TIME_FORMATS.MMM,
      );
      item.day = this.companyUtilService.convertUTCDateWithTimeZone(
        startDateTime,
        true,
        TIME_FORMATS.DD,
      );
      item.week = this.companyUtilService.convertUTCDateWithTimeZone(
        startDateTime,
        true,
        TIME_FORMATS.DDD,
      );
      // Set open event to top
      if (item.status === EVENT_STATUS_VALUE.Open) {
        openEvents.push(item);
      } else {
        otherEvents.push(item);
      }
    });
    if (!isEmpty(openEvents)) {
      this.events = [...openEvents, ...otherEvents];
    } else {
      this.events = [...otherEvents];
    }
    if (
      this.selectedFilter.value !== 0 &&
      this.selectedFilter.value < eventsRes.total
    ) {
      this.total = this.selectedFilter.value;
    } else {
      this.total = eventsRes.total;
    }
    this.isPending = false;
    this.currentPageEvents = slice(this.events, 0, this.pageSize);
    this.cdr.detectChanges();
  }
}
